import {
  cloneMap, DataModelBuilder, isSameData, isSameMap, mapToData, SerializationContext, SerializedData,
} from '../../dto/dto.js';
import dayjs, { Dayjs } from 'dayjs';
import { TimeZone } from '../../../time/timezone.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { EventProfile } from '../event-profile.js';
import { SerialNumberScope } from './serial-number-scope.js';
import { LegacySerialNumberStrategy } from './legacy-serial-number-strategy.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { VersionRange } from '../../core/version-range.js';
import { LegacyStandardSerialNumberPool } from './legacy-standard-serial-number-pool.js';
import { EventTicket } from '../event-ticket.js';

/**
 * @deprecated Use {@link TemplateBasedSerialNumberPool} instead.
 */
export class LegacyTicketBasedSerialNumberPool extends LegacyStandardSerialNumberPool {
  public static readonly TYPE_ID: string = '0e0c0b0a090807060504030201000f0e';
  public static readonly SCHEMA_VERSION: number = 3;

  private constructor(
      businessRef: BusinessModuleRef,
      eventId: string | null,
      creationDate: Dayjs,
      shortCode: string,
      scope: SerialNumberScope,
      totalTicketGroups: number,
      ticketGroups: Map<string, number>,
      serialNumbers: Map<string, number>,
  ) {
    super(
        businessRef,
        eventId,
        creationDate,
        shortCode,
        scope,
        totalTicketGroups,
        ticketGroups,
        serialNumbers,
    );
  }

  get strategy(): LegacySerialNumberStrategy {
    return LegacySerialNumberStrategy.AssignByTicketType;
  }

  get $typeId(): string {
    return LegacyTicketBasedSerialNumberPool.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(LegacyTicketBasedSerialNumberPool.SCHEMA_VERSION);
  }

  /**
   * @deprecated Use only for migration purposes.
   *
   * @return {Map<string, number>} The ticket groups.
   */
  getTicketGroups(): Map<string, number> {
    return this.ticketGroups;
  }

  /**
   * @deprecated Use only for migration purposes.
   *
   * @return {Map<string, number>} The serial numbers.
   */
  getSerialNumbers(): Map<string, number> {
    return this.serialNumbers;
  }

  static forEvent(event: EventProfile): LegacyTicketBasedSerialNumberPool | null {
    if(event.shortCode == null) {
      return null;
    }

    return new LegacyTicketBasedSerialNumberPool(
        event.businessRef,
        event.uniqueId,
        dayjs(),
        event.shortCode,
        SerialNumberScope.Event,
        0,
        new Map<string, number>(),
        new Map<string, number>(),
    );
  }

  static forBusiness(businessRef: BusinessModuleRef, shortCode: string): LegacyTicketBasedSerialNumberPool {
    return new LegacyTicketBasedSerialNumberPool(
        businessRef,
        null,
        dayjs(),
        shortCode,
        SerialNumberScope.Business,
        0,
        new Map<string, number>(),
        new Map<string, number>(),
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): LegacyTicketBasedSerialNumberPool {
    return new LegacyTicketBasedSerialNumberPool(
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        MapUtils.getOrNull<string>(map, 'eventId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        MapUtils.get<string>(map, 'shortCode'),
        toEnumFromString(MapUtils.get<string>(map, 'scope'), SerialNumberScope),
        MapUtils.get<number>(map, 'totalTicketGroups'),
        MapUtils.getMap(map, 'ticketGroups', (k, v) => ({ key: k, value: Number(v) })),
        MapUtils.getMap(map, 'serialNumbers', (k, v) => ({ key: k, value: Number(v) })),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'eventId': this.eventId,
      'businessRef': this.businessRef.encode(),
      'creationDate': context.encodeDate(this.creationDate),
      'shortCode': this.shortCode,
      'scope': fromEnumAsString(this.scope),
      'totalTicketGroups': this.totalTicketGroups,
      'ticketGroups': mapToData(this.ticketGroups, (k, v) => ({ key: k, value: v })),
      'serialNumbers': mapToData(this.serialNumbers, (k, v) => ({ key: k, value: v })),
    };
  }

  clone(): this {
    return new LegacyTicketBasedSerialNumberPool(
        this.businessRef.clone(),
        this.eventId,
        TimeZone.cloneDate(this.creationDate),
        this.shortCode,
        this.scope,
        this.totalTicketGroups,
        cloneMap(this.ticketGroups, (v) => v),
        cloneMap(this.serialNumbers, (v) => v),
    ) as this;
  }

  isSame(other: this): boolean {
    if(this === other) {
      return true;
    }

    return this.eventId === other.eventId &&
        isSameData(this.businessRef, other.businessRef) &&
        this.creationDate == other.creationDate &&
        this.shortCode === other.shortCode &&
        this.scope === other.scope &&
        this.totalTicketGroups === other.totalTicketGroups &&
        isSameMap(other.ticketGroups, this.ticketGroups, (lh, rh) => lh === rh) &&
        isSameMap(other.serialNumbers, this.serialNumbers, (lh, rh) => lh === rh);
  }

  protected getTicketGroupId(ticket: EventTicket): string | null {
    return ticket.template.uniqueId;
  }

  protected getTicketGroup(ticket: EventTicket): number {
    return this.totalTicketGroups + 1;
  }
}

export class LegacyTicketBasedSerialNumberPoolBuilder extends DataModelBuilder<LegacyTicketBasedSerialNumberPool> {
  build(context: SerializationContext, map: SerializedData): LegacyTicketBasedSerialNumberPool {
    return LegacyTicketBasedSerialNumberPool.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(LegacyTicketBasedSerialNumberPool.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(LegacyTicketBasedSerialNumberPool.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version: number = versionRange.to;

    if(version < 2) {
      map['scope'] = fromEnumAsString(SerialNumberScope.Event);
      map['shortCode'] = map['eventShortCode'];
      version = 2;
    }

    if(version < 3) {
      map['totalTicketGroups'] = map['totalTicketTypes'];
      map['ticketGroups'] = map['ticketTypes'];
      version = 3;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
